import { FC, useRef, useEffect, useState } from 'react';
import Typography from '@components/common/typography';
import styles from './styles.module.scss';
import illustration from '@svg/illustrations/scan_qr.svg';
import { IModalComponentProps } from '@context/modal_context';
import { useDebounceValue } from 'usehooks-ts';
import { toast } from 'react-toastify';
import { useGiftCardsModals } from '@hooks/modals/useGiftCardsModals';
import Loader from '@components/loader';

const ScanGiftCardModal: FC<IModalComponentProps> = ({ modalData, changeModalData }) => {
    const hiddenInputRef = useRef<HTMLInputElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [debouncedScanValue, setDebouncedScanValue] = useDebounceValue('', 500);
    const { handleOpenRedeemGiftCardModal } = useGiftCardsModals({
        refetchGiftCards: modalData?.refetchGiftCards
    });

    useEffect(() => {
        if (hiddenInputRef.current) {
            hiddenInputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        if (debouncedScanValue.trim()) {
            try {
                const result = debouncedScanValue.trim().split('redeem_id=')?.[1];
                handleOpenRedeemGiftCardModal(result);
            } catch (error) {
                console.error('Error processing QR code:', error);
                toast.error('Cannot scan the QR code');
            } finally {
                if (hiddenInputRef.current) {
                    hiddenInputRef.current.value = '';
                }
                setDebouncedScanValue('');
                setIsLoading(false);
            }
        }
    }, [debouncedScanValue]);

    const handleScanInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const scannedValue = event.target.value;
        if (scannedValue.trim()) {
            setIsLoading(true);
            setDebouncedScanValue(scannedValue);
        }
    };

    const handleContainerClick = () => {
        if (hiddenInputRef.current) {
            hiddenInputRef.current.focus();
        }
    };

    return (
        <div className={styles.wrapper} onClick={handleContainerClick}>
            {isLoading ? <Loader /> : <img src={illustration} alt="" />}
            <Typography variant="h4" className={styles.title}>
                Scan QR code
            </Typography>
            <Typography variant="body3" className={styles.description} alignment="center">
                To start redemption, please scan the gift card QR code provided by the customer
            </Typography>

            {/* Hidden input to capture QR scanner input */}
            <input
                ref={hiddenInputRef}
                type="text"
                className={styles.hiddenInput}
                onChange={handleScanInput}
                placeholder=""
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
            />
        </div>
    );
};

export default ScanGiftCardModal;
